import React, { useState, useMemo } from 'react';
import ProzoDataGrid from "../../common/ProzoDataGrid";
import { Box, Button, Chip, Grid, Paper, Stack, Tooltip, IconButton, Typography } from '@mui/material';
import StatusChip from '../../common/StatusChip';
import OrderRowActions from '../../common/OrderRowActions';
import PaymentBadge from '../../common/PaymentBadge';
import ZoneBadge from '../../common/ZoneBadge';
import { formatCurrency, formatDate, formatNumber, maskAddress, parseDateAndTime, shouldMaskData } from '../../helpers/UtilityHelper';
import { delivered, deliveryFailed, rto, transit, placed, cancelled, lost } from '../../common/util';
import palette from '../../../theme/palette';
import { Link } from 'react-router-dom'
import _ from "lodash"
import FilterBar from '../../common/FilterBar';
import SyncIcon from '@mui/icons-material/Sync';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FunctionsIcon from '@mui/icons-material/Functions';
import useNdr from "../../hooks/useNdr"
import Loader from '../../common/Loader';
// import Page from "../../components/Page"
import { apiConstants, post } from '../../common/constants';
import { recalculatePricing } from '../../common/recalculatePricing';
import { useSnackbar } from 'notistack';
import CreateIcon from '@mui/icons-material/Create';
import Remarks from '../Remarks/Remarks';
import MUIModal from '../../components/MuiModal';
import ResyncActionModal from './ResyncActionModal';
import Pods from '../B2BOrder/Pods';
import CancelOrder from './CancelOrder';
import CancelIcon from '@mui/icons-material/Cancel';
import PageTitle from '../../components/PageTitle';
import { useEffect } from 'react';
import { Timelapse } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchReportData } from '../../redux/actions/reportAction';
import { getToken } from '../../helpers/ReduxHelpers';
import jwt_decode from "jwt-decode";
import { useGetAllCourierPartnersCachedQuery } from '../../redux/commonApiCalls';
import { ALL_SERVICE_TYPES, COURIER_LIST_QUERY_PAYLOAD } from '../../common/ProshipFormComponents';
import NetraScoreBadge, { getNetraBandColor } from './NetraScoreBadge';
import RtoRiskBadge from './RtoRiskBadge';
import CopyIcon from '../../common/CopyIcon';

// NETRA RTO band -> text colour (green low risk -> red high risk).
const RTO_BAND_TEXT_COLOR = { GREEN: '#2e7d32', YELLOW: '#f9a825', ORANGE: '#ed6c02', RED: '#d32f2f', GREY: '#757575' };

// Shared with the Order Details banner tooltip.
const KCI_IMPACTED_TOOLTIP = 'This shipment was tagged by a KCI (Keep Customers Informed) event — its delay is attributed to a known external disruption.';

function getStatusColor(status) {
    if (!status) return 'default';
    const s = String(status).toUpperCase();
    if (s === 'DELIVERED') return 'success';
    if (s.startsWith('RTO')) return 'error';
    if (s.includes('NDR') || s.includes('UNDELIVERED')) return 'warning';
    if (s.includes('CANCEL')) return 'default';
    return 'info';
}

const CellLine = ({ children, muted, bold, ellipsis }) => (
    <Box
        sx={{
            fontSize: 12.5,
            lineHeight: 1.4,
            color: muted ? 'text.secondary' : 'text.primary',
            fontWeight: bold ? 600 : 400,
            ...(ellipsis ? { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } : {}),
        }}
    >
        {children}
    </Box>
);

const CellStack = ({ children }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35, width: '100%', py: 0.5 }}>{children}</Box>
);

// ─── Cell-level UI components ─────────────────────────────────────────────────



const AwbLink = ({ awb, id }) => (
    <Box
        component={Link}
        to={`/order-detail/${awb}?id=${id}`}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
            fontSize: '12px',
            fontWeight: 700,
            letterSpacing: '0.02em',
            color: '#1d4ed8',
            textDecoration: 'none',
            borderBottom: '1px dashed #93c5fd',
            pb: '1px',
            transition: 'color 0.15s, border-color 0.15s',
            '&:hover': { color: '#1e40af', borderBottomStyle: 'solid', borderBottomColor: '#1d4ed8' },
        }}
    >
        {awb}
    </Box>
);

const DataLabel = ({ children }) => (
    <Box component="span" sx={{ fontSize: '10px', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.4px', mr: '4px' }}>
        {children}
    </Box>
);

function MerchantCell({ name }) {
    if (!name) return <Box sx={{ fontSize: '12px', color: 'text.disabled' }}>—</Box>;
    return <CellLine bold>{name}</CellLine>;
}

// ─────────────────────────────────────────────────────────────────────────────


const AllOrders = (props) => {
    const [page, setPage] = useState(0);
    const [filterChange, setFiltersChange] = useState(0);
    const [filters, setFilters] = useState(0);
    const [sizePerPage, setSizePerPage] = useState(50);
    const { enqueueSnackbar } = useSnackbar();
    const [reload, setReload] = useState(0)
    const [open, setOpen] = useState(false);
    const [id, setId] = useState();
    const [pods, setPods] = useState(false);
    const [Cancell, setCancell] = useState(false)
    const [isCalculating, setIsCalculating] = useState(false);
    const [rowSelectionModel, setRowSelectionModel] = useState([]);
    const [awbNumber, setAwbNumber] = useState()
    const [resyncModal, setResyncModal] = useState({ open: false, awb: null, reference: null });
    const { success } = useSelector((state) => state.user);
    const userToken = jwt_decode(getToken());
    const merchantId = userToken && userToken ? userToken : "";
    const dispatch = useDispatch()
    const { report } = useSelector(state => state);
    const { piiAccessAllow } = jwt_decode(getToken());

    // Cached courier_partners list — used to resolve a (parent, serviceType)
    // filter pair into the set of matching courier.$id values. Same RTK cache
    // key as CourierFilter on the same page → no extra API call.
    const { data: courierPartnersForFilter = [] } = useGetAllCourierPartnersCachedQuery(COURIER_LIST_QUERY_PAYLOAD);

    useEffect(() => {
        if (success) {
            dispatch(fetchReportData(merchantId.merchantOid))
        }
    }, [success])

    // const [revered,setReversed] = useState(false)

    // const [revered,setReversed] = useState(false)

    const request = useMemo(() => {
        // const exclude = "pickupDetails.from_name,pickupDetails.from_phone_number,pickupDetails.from_address,pickupDetails.pickupTime,courier,merchant,version,order_history,itemList,orderManifest,invoiceUrl,courierPricingRTO,courierPricing,overriddenCourierAccountCode,actualCourierProviderName,isDiscrepancyRaised,discrepancyId,securityKey,sortCode,channelType,channelName,createdByUser,skipRule,modifiedByUser,rvp_reason,channel_name,customerDetail,cpErrorList,mongoLabelSignedUrlDetails,overriddenCourierRule,mongoInvoiceSignedUrlDetails,merchantPricing.pricingObj,merchantPricing.merchantRateCard,modifiedByUser,lastModifiedDate,terminalStatus";
        const includes = "id,reference,invoiceValue,pods,shippingLabelId,awbRegisteredDate,merchantPricing.zone,merchantDetail,courierDetail,pickupDetails.from_pincode,pickupDetails.from_city,deliveryDetails.to_pincode,deliveryDetails.to_state,deliveryDetails.to_city,channelName,orderStatus,actualCourierProviderName,shipmentDetail,paymentMode,awbNumber,referenceNumber,merchantPricing.price,qcDetails,netraAddress,netraRto,orderTags,cpAddressVerified"
        const andfilter = [];
        const orFilter = [];
        andfilter.push({ "field": "orderSubStatus", operator: "ne", value: "UNPROCESSED" })
        andfilter.push({ "field": "orderType", operator: "eq", value: "FORWARD SHIPMENT" })
        andfilter.push({ "field": "shipmentType", operator: "eq", value: 'B2C' });
        andfilter.push({ "field": "merchantId", operator: "ne", value: 260425082022 })

        if (Array.isArray(filters.status) ? filters.status.length > 0 : filters.status) {
            const statusList = Array.isArray(filters.status) ? filters.status : [filters.status];
            andfilter.push({ "field": "orderStatus", "operator": "in", "value": statusList })
        }
        if (filters.awb) {
            andfilter.push({ "field": "awbNumber", "operator": "eq", "value": filters.awb });
        }
        if (filters.rtoAwb) {
            andfilter.push({ "field": "rtoAwb", "operator": "eq", "value": filters.rtoAwb });
        }
        if (filters.city) {
            andfilter.push({ "field": "deliveryDetails.to_city", "operator": "eq", "value": filters.city });
        }
        if (filters.state) {
            andfilter.push({ "field": "deliveryDetails.to_state", "operator": "eq", "value": filters.state });
        }
        if (filters.merchant) {
            andfilter.push({ "field": "merchant.$id", "operator": "eq", "value": 'ObjectId(\'' + filters.merchant + '\')' });
        }
        // Parent + Service Type (+ optional Account Code) — chained filter
        // that replaces the old single-step Courier filter on this page.
        //   1. parent + serviceType[] are REQUIRED. If either is empty/[],
        //      skip the entire clause (incomplete selection).
        //   2. Find candidate couriers whose parent matches AND whose
        //      serviceTypePrimary OR serviceTypeSecondary is in serviceType[].
        //   3. If accountCode[] is also picked, narrow candidates further to
        //      those cpAccountCodes. Empty accountCode[] = use all candidates.
        //   4. Push their `_id`s as `courier.$id in [...]` — backend just sees
        //      a normal courier-IDs filter, no idea about parent/serviceType.
        const hasServiceType = Array.isArray(filters.serviceType) && filters.serviceType.length > 0;
        if (filters.parent && hasServiceType && Array.isArray(courierPartnersForFilter) && courierPartnersForFilter.length > 0) {
            // "All Service Types" sentinel — when present, skip the service-type
            // narrowing and consider every courier under the parent.
            const includesAll = filters.serviceType.includes(ALL_SERVICE_TYPES);
            const candidateCouriers = courierPartnersForFilter.filter((c) => {
                // Scope to B2C only — this is the D2C forward page. Without this a
                // B2B courier that shares a parent name + service type would leak
                // into the resolved courier IDs (mirrors the RVP page's B2C scope).
                // Check both field spellings the API may return.
                if (c?.supported_shipment !== 'B2C' && c?.supportedShipmentType !== 'B2C') return false;
                if (c?.parent !== filters.parent) return false;
                if (includesAll) return true;
                const primaryHit = c?.serviceTypePrimary && filters.serviceType.includes(c.serviceTypePrimary);
                const secondaryHit = c?.serviceTypeSecondary && filters.serviceType.includes(c.serviceTypeSecondary);
                return primaryHit || secondaryHit;
            });
            const hasAccountCode = Array.isArray(filters.accountCode) && filters.accountCode.length > 0;
            const finalCouriers = hasAccountCode
                ? candidateCouriers.filter((c) => filters.accountCode.includes(c?.cpAccountCode))
                : candidateCouriers;
            const matchedIds = finalCouriers.map((c) => 'ObjectId(\'' + c.id + '\')');
            if (matchedIds.length > 0) {
                andfilter.push({ field: 'courier.$id', operator: 'in', value: matchedIds });
            } else {
                // No couriers survived the chain — defensive. Push impossible
                // value so query returns zero results rather than showing all.
                andfilter.push({ field: 'courier.$id', operator: 'in', value: ['ObjectId(\'no-match\')'] });
            }
        }
        if (filters.reference) {
            orFilter.push({ "field": "reference", "operator": "eq", "value": filters.reference });
            orFilter.push({ "field": "shippingLabelId", "operator": "eq", "value": filters.reference });
        }
        if (filters.fromDate) {
            andfilter.push({ field: "orderDate", operator: "gte", value: formatDate(filters.fromDate, 'yyyy-MM-dd') })
        }
        if (filters.toDate) {
            andfilter.push({ field: "orderDate", operator: "lte", value: formatDate(filters.toDate, 'yyyy-MM-dd') })
        }
        if (filters.pickupPincode) {
            andfilter.push({ field: "pickupDetails.from_pincode", operator: "eq", value: filters.pickupPincode })
        }
        if (Array.isArray(filters.zone) ? filters.zone.length > 0 : filters.zone) {
            const zoneList = Array.isArray(filters.zone) ? filters.zone : [filters.zone];
            andfilter.push({ field: "merchantPricing.zone", operator: "in", value: zoneList })
        }
        if (filters.paymentMode) {
            andfilter.push({ field: "paymentMode", operator: "eq", value: filters.paymentMode })
        }
        const filter = {
            "andfilter": andfilter,
            "orfilter": orFilter,
            "offset": (page) * sizePerPage,
            "limit": sizePerPage,
            // "controllerFilter": true,
            "skipCount": true,
            "includes": includes,
            "sort": { "field": "awbRegisteredDate", "direction": "DESC" }
            // "excludes": exclude,
        }
        return filter;
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterChange, page, sizePerPage, reload])


    let { data = [], isFetching, isLoading } = useNdr({ request: request });

    // One call: forward, plus RTO when the order has gone RTO. The row refreshes when done.
    const handleRecal = (id) => {
        setIsCalculating(true);
        recalculatePricing({
            orderId: id
        })
            .then(() => {
                enqueueSnackbar("Order value recalculated", { variant: 'success' });
                setReload((r) => r + 1);
            })
            .catch((err) => enqueueSnackbar(err.message, { variant: err.variant || 'error' }))
            .finally(() => setIsCalculating(false));
    }

    const handleRecalculateSLA = (id) => {
        setIsCalculating(true);

        let url = apiConstants.RECALCULATE_SLA + "?awsList=" + id
        post(url)
            .then((res) => {
                if (res.status === 200) {
                    enqueueSnackbar("Done", { variant: 'success' });
                    setIsCalculating(false);
                    setReload(reload + 1);
                }
            })
            .catch((err) => {
                enqueueSnackbar("Something went wrong", { variant: 'error' });
                setIsCalculating(false);
                console.log(err)
            });
    }


    const statusOptions = useMemo(() => {
        const all = [...deliveryFailed, ...rto, ...transit, ...delivered, ...placed, ...cancelled, ...lost];
        return _.orderBy(all.map((op) => ({ label: _.startCase(op), value: op })), 'value');
    }, []);

    const handleClickOpen = (id) => {
        setOpen(true);
        setId(id);
        // console.log(artifactId)
    };
    const handleClickCancel = (id) => {
        setCancell(true);
        setId(id);
        // console.log(artifactId)
    };


    const handleClose = () => {
        setOpen(false);
        setPods(false)
        setCancell(false);
        // setReversed(false)
    };

    const handleOpen = (row) => {
        setPods(true)
        setId(row)
    }

    useEffect(() => {
        if (data.data?.length > 0) {
            const awb_number = data?.data
                .filter((val) => rowSelectionModel.includes(val.id))
                .map((order) => order.awb_number);
            setAwbNumber(awb_number)
        }
    }, [rowSelectionModel, data]);

    const recalculateEddFunc = () => {
        setIsCalculating(true)
        post((`${apiConstants.RECALCULATE_EDD}?awsList=${awbNumber}`)).then((res) => {
            if (res.status === 200) {
                setIsCalculating(false)
                enqueueSnackbar("EDD Recalculation Done", { variant: 'success' })
                // setReload(reload + 1);
                setAwbNumber([])
                setRowSelectionModel([])
            }
        })
            .catch((err) => {
                console.log(err)
                enqueueSnackbar(err.response?.data?.message, { variant: 'error' })
                setIsCalculating(false)
            })
    }

    const columns = [
        {
            field: 'merchant',
            headerName: 'Merchant', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 160, flex: 1.2,
            renderCell: ({ row }) => <MerchantCell name={row?.merchantDetail?.name} />,
        },
        {
            field: 'order_details',
            headerName: 'Shipment Details', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 180, flex: 1.2,
            renderCell: ({ row }) => (
                <CellStack>
                    <CellLine bold>{row.reference || '—'}</CellLine>
                    {row.shippingLabelId && row.reference !== row.shippingLabelId && (
                        <Box sx={{ fontSize: '11px', color: 'text.secondary' }}>
                            {row.shippingLabelId}
                        </Box>
                    )}
                    {row.awbRegisteredDate && (
                        <Box sx={{ fontSize: '11px', color: '#9ca3af' }}>
                            {parseDateAndTime(row.awbRegisteredDate)}
                        </Box>
                    )}
                    {(row.orderTags || []).some((t) => String(t).toUpperCase().includes('KCI')) && (
                        <Tooltip title={KCI_IMPACTED_TOOLTIP}>
                            <Chip label="KCI Impacted" size="small"
                                sx={{ alignSelf: 'flex-start', mt: 0.25, height: 18, fontSize: 10, fontWeight: 700, bgcolor: '#FFF3E0', color: '#E65100', border: '1px solid #E6510055' }} />
                        </Tooltip>
                    )}
                    {row.cpAddressVerified === true && (
                        <Tooltip title={`Verified Address — ${row.courierDetail?.parent || 'the courier partner'} verified this drop address at booking (high delivery confidence).`}>
                            <Chip label="Verified Address" size="small"
                                sx={{ alignSelf: 'flex-start', mt: 0.25, height: 18, fontSize: 10, fontWeight: 700, bgcolor: '#E8F5E9', color: '#1B5E20', border: '1px solid #1B5E2055' }} />
                        </Tooltip>
                    )}
                </CellStack>
            ),
        },
        {
            field: 'statusDetails',
            headerName: 'Status', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 155, flex: 1,
            renderCell: ({ row }) => (
                <Stack spacing={0.5} alignItems="flex-start">
                    <StatusChip status={row.orderStatus} />
                </Stack>
            ),
        },
        {
            field: 'addressQuality',
            headerName: 'Address Quality', headerAlign: 'center', align: 'center',
            minWidth: 140, flex: 0.9,
            sortable: true,
            valueGetter: (params) => params.row?.netra_address?.score ?? -1,
            renderCell: ({ row }) => {
                const netra = row.netra_address
                const hasAddr = netra && netra.score !== null && netra.score !== undefined
                if (!hasAddr) {
                    return <Box sx={{ fontSize: '11px', color: 'text.disabled' }}>—</Box>
                }
                const band = netra?.band || 'UNKNOWN'
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, width: '100%', py: 0.5 }}>
                        <NetraScoreBadge netra={netra} orderId={row.id} />
                        <Typography sx={{ fontSize: '11px', fontWeight: 700, color: getNetraBandColor(band), textTransform: 'capitalize', lineHeight: 1 }}>
                            {band.toLowerCase()}
                        </Typography>
                    </Box>
                )
            },
        },
        {
            field: 'rtoRisk',
            headerName: 'RTO Risk', headerAlign: 'center', align: 'center',
            minWidth: 120, flex: 0.8,
            sortable: true,
            valueGetter: (params) => params.row?.netra_rto?.score ?? -1,
            renderCell: ({ row }) => {
                const rto = row.netra_rto
                const hasRto = rto && rto.score !== null && rto.score !== undefined
                if (!hasRto) {
                    return <Box sx={{ fontSize: '11px', color: 'text.disabled' }}>—</Box>
                }
                const band = rto?.band || ''
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, width: '100%', py: 0.5 }}>
                        <RtoRiskBadge netra={rto} orderId={row.id} />
                        <Typography sx={{ fontSize: '11px', fontWeight: 700, color: RTO_BAND_TEXT_COLOR[band] || 'text.secondary', textTransform: 'capitalize', lineHeight: 1 }}>
                            {band.toLowerCase()}
                        </Typography>
                    </Box>
                )
            },
        },
        {
            field: 'zone',
            headerName: 'Zone', headerAlign: 'center', align: 'center',
            sortable: true, width: 130,
            valueGetter: (params) => params.row?.merchant_pricing?.zone || '',
            renderCell: ({ row }) => <ZoneBadge zone={row.merchant_pricing?.zone} />,
        },
        {
            field: 'courier',
            headerName: 'Courier & AWB', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 190, flex: 1.4,
            renderCell: ({ row }) => {
                const courierParent = row.courierDetail?.parent?.includes('Blended')
                    ? row.actualCourierProviderName
                    : row.courierDetail?.parent;
                return (
                    <CellStack>
                        {/* Courier name — primary identity */}
                        {courierParent
                            ? <CellLine bold>{courierParent}</CellLine>
                            : <CellLine muted>Unknown courier</CellLine>
                        }
                        {/* AWB — labeled tracking link */}
                        {row.awb_number
                            ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                                    <AwbLink awb={row.awb_number} id={row.id} />
                                    <CopyIcon text={row.awb_number} />
                                </Box>
                            )
                            : <Box sx={{ fontSize: '11px', color: 'text.disabled' }}>—</Box>
                        }
                        {/* ProzoId */}
                        {row.courierDetail?.prozoId && (
                            <CellLine muted ellipsis>{row.courierDetail.prozoId}</CellLine>
                        )}
                    </CellStack>
                );
            },
        },
        {
            field: 'pickup',
            headerName: 'Pickup', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 120, flex: 0.9,
            renderCell: ({ row }) => (
                <CellStack>
                    {row.pickup_details?.from_city && <CellLine bold>{row.pickup_details.from_city}</CellLine>}
                    {row.pickup_details?.from_pincode && (
                        <Box sx={{ fontSize: '11px', color: '#6b7280' }}>
                            {row.pickup_details.from_pincode}
                        </Box>
                    )}
                </CellStack>
            ),
        },
        {
            field: 'delivery',
            headerName: 'Delivery', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 150, flex: 1.1,
            renderCell: ({ row }) => {
                const cityState = row.delivery_details?.to_city && row.delivery_details?.to_state
                    ? `${row.delivery_details.to_city}, ${row.delivery_details.to_state}`
                    : null;
                const masked = shouldMaskData(report, row, piiAccessAllow);
                return (
                    <CellStack>
                        {cityState && <CellLine bold>{masked ? maskAddress(cityState) : cityState}</CellLine>}
                        {row.delivery_details?.to_pincode && (
                            <Box sx={{ fontSize: '11px', color: '#6b7280' }}>
                                {row.delivery_details.to_pincode}
                            </Box>
                        )}
                    </CellStack>
                );
            },
        },
        {
            field: 'shipment',
            headerName: 'Package', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 150, flex: 1.1,
            renderCell: ({ row }) => {
                const s = row.shipment_detail?.[0];
                if (!s) return <Box sx={{ fontSize: '12px', color: 'text.disabled' }}>—</Box>;
                const dead = (s.item_weight / 1000).toFixed(2);
                const vol = ((s.item_length * s.item_breadth * s.item_height) / 5000).toFixed(2);
                const dim = `${s.item_length}×${s.item_breadth}×${s.item_height} cm`;
                return (
                    <CellStack>
                        <Box sx={{ fontSize: '12.5px', color: 'text.primary' }}>
                            <DataLabel>Dead</DataLabel>{dead} kg
                        </Box>
                        <Box sx={{ fontSize: '12px', color: 'text.secondary' }}>
                            <DataLabel>Vol</DataLabel>{vol} kg
                        </Box>
                        <Box sx={{ fontSize: '11px', color: '#9ca3af', fontFamily: 'monospace' }}>{dim}</Box>
                    </CellStack>
                );
            },
        },
        {
            field: 'paymentDetails',
            headerName: 'Payment', headerAlign: 'left', align: 'left',
            sortable: true, minWidth: 120, flex: 0.9,
            valueGetter: (params) => params.row?.payment_mode || '',
            renderCell: ({ row }) => (
                <CellStack>
                    <Box sx={{ fontSize: '13.5px', fontWeight: 700, color: 'text.primary', letterSpacing: '-0.01em' }}>
                        {formatCurrency(row.invoice_value)}
                    </Box>
                    <PaymentBadge mode={row.payment_mode} />
                </CellStack>
            ),
        },
        {
            field: 'pod',
            headerName: 'POD', headerAlign: 'center', align: 'center',
            hideSortIcons: true, width: 80,
            renderCell: ({ row }) => {
                if (!row.pods) return <Box sx={{ fontSize: '12px', color: 'text.disabled' }}>—</Box>;
                return (
                    <Box
                        component="button"
                        onClick={() => handleOpen(row)}
                        sx={{
                            display: 'inline-flex', alignItems: 'center', px: '8px', py: '3px',
                            borderRadius: '4px', fontSize: '11px', fontWeight: 600, cursor: 'pointer',
                            bgcolor: 'transparent', color: '#2563eb', border: '1px solid #bfdbfe',
                            transition: 'all 0.15s',
                            '&:hover': { bgcolor: '#eff6ff', borderColor: '#93c5fd' },
                        }}
                    >
                        View
                    </Box>
                );
            },
        },
        {
            field: 'action',
            headerName: '', headerAlign: 'center', align: 'center',
            hideSortIcons: true, width: 60,
            renderCell: ({ row }) => (
                <OrderRowActions
                    actions={[
                        {
                            key: 'view',
                            label: 'View details',
                            icon: <VisibilityIcon fontSize="small" />,
                            href: `/order-detail/${row.awb_number}?id=${row.id}`,
                            target: '_blank',
                        },
                        {
                            key: 'remarks',
                            label: 'Add / edit remarks',
                            icon: <CreateIcon fontSize="small" />,
                            onClick: () => handleClickOpen(row.id),
                        },
                        {
                            key: 'cancel',
                            label: 'Cancel order',
                            icon: <CancelIcon fontSize="small" />,
                            onClick: () => handleClickCancel(row),
                            color: 'error',
                            hidden: row.lifeCycleComplete === true,
                            dividerBefore: true,
                        },
                        {
                            key: 'resync',
                            label: 'Resync order',
                            icon: <SyncIcon fontSize="small" />,
                            onClick: () => setResyncModal({ open: true, awb: row.awb_number, reference: row.reference }),
                            dividerBefore: true,
                        },
                        {
                            key: 'recalculate',
                            label: 'Recalculate order value',
                            icon: <FunctionsIcon fontSize="small" />,
                            onClick: () => handleRecal(row.id),
                        },
                        {
                            key: 'recalculate-sla',
                            label: 'Recalculate SLA',
                            icon: <Timelapse fontSize="small" />,
                            onClick: () => handleRecalculateSLA(row.awb_number),
                        },
                    ]}
                />
            ),
        },
    ];



    return (
        <>
            <PageTitle>Forward Shipments</PageTitle>
            <MUIModal open={open} handleClose={handleClose} children={<Remarks id={id} on={"ORDER"} setReload={setReload} reload={reload} setOpen={setOpen} />}
                title={"Update Remarks"} />
            <MUIModal open={Cancell} handleClose={handleClose} children={<CancelOrder id={id} setReload={setReload} reload={reload} setCancell={setCancell} />}
                title={"Cancel Order"} hideClose={true} maxWidth={'md'} />
            <MUIModal open={pods} handleClose={handleClose} children={<Pods id={id} />} title={"PODs Link"} />
            <ResyncActionModal
                open={resyncModal.open}
                awb={resyncModal.awb}
                reference={resyncModal.reference}
                onClose={() => setResyncModal({ open: false, awb: null, reference: null })}
                onDone={() => setReload((r) => r + 1)}
            />
            {(isFetching || isLoading || isCalculating) && <Loader />}

            {/* ── Filter bar ────────────────────────────────────────────────── */}
            <FilterBar
                show={['merchant', 'parent', 'serviceType', 'accountCode', 'reference', 'awb', 'rtoAwb', 'city', 'state', 'dateRange', 'status', 'pickupPincode', 'zone', 'paymentMode']}
                statusOptions={statusOptions}
                courierType="B2C"
                onApply={(data) => {
                    setFilters(data);
                    setFiltersChange((f) => f + 1);
                    setPage(0);
                }}
                onReset={() => {
                    setFilters({});
                    setFiltersChange((f) => f + 1);
                    setPage(0);
                }}
            />

            {/* ── Address Quality KPI Cards — distribution for current page ── */}
            {data?.data?.length > 0 && (() => {
                const rows = data.data
                const scored = rows.filter((r) => r.netra_address?.score != null)
                const total = scored.length
                const unscored = rows.length - total
                const excellent = scored.filter((r) => r.netra_address.band === 'EXCELLENT').length
                const good = scored.filter((r) => r.netra_address.band === 'GOOD').length
                const fair = scored.filter((r) => r.netra_address.band === 'FAIR').length
                const poor = scored.filter((r) => r.netra_address.band === 'POOR').length
                const kpis = [
                    {label: 'Scored', value: total, color: palette.primary.main, sub: `of ${formatNumber(rows.length)} shipments`},
                    {label: 'Excellent', value: excellent, color: palette.success.dark, sub: total ? `${Math.round((excellent / total) * 100)}% of scored` : '—'},
                    {label: 'Good', value: good, color: palette.info.main, sub: total ? `${Math.round((good / total) * 100)}% of scored` : '—'},
                    {label: 'Fair', value: fair, color: palette.warning.dark, sub: total ? `${Math.round((fair / total) * 100)}% of scored` : '—'},
                    {label: 'Poor', value: poor, color: palette.error.main, sub: total ? `${Math.round((poor / total) * 100)}% of scored` : '—'},
                    {label: 'Not Scored', value: unscored, color: palette.grey[500], sub: `of ${formatNumber(rows.length)} shipments`},
                ]
                return (
                    <Box sx={{mb: 2}}>
                        <Typography sx={{fontSize: '0.78rem', fontWeight: 600, color: palette.grey[800], mb: 0.5}}>
                            Address Quality — {formatNumber(rows.length)} shipments on this page
                        </Typography>
                        <Typography sx={{fontSize: '0.68rem', color: palette.grey[500], mb: 1.5}}>
                            NETRA address quality scores for the {formatNumber(rows.length)} shipments currently displayed. Navigate pages to see updated distribution.
                        </Typography>
                        <Grid container spacing={1.5}>
                            {kpis.map((k) => (
                                <Grid item xs={6} sm={4} md={2} key={k.label}>
                                    <Paper elevation={0} sx={{
                                        p: 1.5, borderRadius: '10px', border: `1px solid ${palette.grey[300]}`,
                                        borderTop: `3px solid ${k.color}`, textAlign: 'center',
                                    }}>
                                        <Typography sx={{fontSize: '1.4rem', fontWeight: 800, color: k.color, lineHeight: 1.2}}>
                                            {formatNumber(k.value)}
                                        </Typography>
                                        <Typography sx={{fontSize: '0.72rem', fontWeight: 600, color: palette.grey[600], textTransform: 'uppercase', letterSpacing: '0.3px'}}>
                                            {k.label}
                                        </Typography>
                                        <Typography sx={{fontSize: '0.68rem', color: palette.grey[500], mt: 0.25}}>
                                            {k.sub}
                                        </Typography>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                )
            })()}

            {/* ── Selection toolbar ─────────────────────────────────────────── */}
            {rowSelectionModel.length > 0 && (
                <Box sx={{ mb: 1.5, px: 2.5, py: 1.25, bgcolor: '#e3f2fd', borderRadius: '8px', border: '1px solid #bbdefb', display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1565c0' }}>
                        {rowSelectionModel.length} order{rowSelectionModel.length !== 1 ? 's' : ''} selected
                    </Typography>
                    <Button type="button" variant="contained" size="small"
                        onClick={recalculateEddFunc}
                        sx={{ ml: 'auto', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', px: 2.5, bgcolor: '#1565c0', '&:hover': { bgcolor: '#0d47a1', boxShadow: 'none' } }}>
                        Recalculate EDD
                    </Button>
                </Box>
            )}

            <ProzoDataGrid
                columns={columns}
                autoHeight={true}
                rows={(data?.data && data?.data.length > 0) ? data?.data : []}
                checkboxSelection={true}
                sizePerPage={sizePerPage}
                setSizePerPage={setSizePerPage}
                rowSelectionModel={rowSelectionModel}
                setRowSelectionModel={setRowSelectionModel}
                setPage={setPage}
                page={page}
                pagination={true}
                getRowHeight={() => 'auto'}
                rowCount={Number.MAX_VALUE}
                hideCountNumber={true}
                getRowId={(row) => row.id}
            />
        </>
    );
};

export default AllOrders
